/**
 * Low-level Web Audio synthesis: oscillator tones, frequency sweeps and
 * short noise bursts, plus a three-drum kit built from them. This is the one
 * layer of the audio module that actually touches `AudioContext` — both
 * `engine.ts` (playing `composition.ts`'s note data) and `sfx.ts` (one-off
 * game events) are built entirely out of these primitives, so there is
 * exactly one place voices are actually synthesized.
 *
 * Everything here is genuinely synthesized — oscillators and generated noise
 * only, zero audio assets — so there is nothing to license and nothing to
 * ship (`docs/DESIGN.md`'s Legal section: "All art and audio are original").
 */
import type { VoiceId } from './composition.js';

type OscType = 'sine' | 'square' | 'triangle' | 'sawtooth';

const VOICE_TYPE: Readonly<Record<VoiceId, OscType>> = {
  square: 'square',
  triangle: 'triangle',
  saw: 'sawtooth',
};

/** Waveforms are not perceptually equal-loudness at the same amplitude — this evens them out a little. */
const VOICE_GAIN_SCALE: Readonly<Record<VoiceId, number>> = {
  square: 0.5,
  triangle: 0.75,
  saw: 0.42,
};

export interface ToneOptions {
  readonly freq: number;
  readonly type: OscType;
  readonly startSec: number;
  readonly durationSec: number;
  readonly peakGain: number;
  readonly attackSec?: number;
  readonly releaseSec?: number;
}

/** An oscillator with a short linear attack/release envelope — never a hard on/off click. */
export function playTone(ctx: BaseAudioContext, destination: AudioNode, opts: ToneOptions): void {
  const osc = ctx.createOscillator();
  osc.type = opts.type;
  osc.frequency.setValueAtTime(Math.max(1, opts.freq), opts.startSec);

  const gain = ctx.createGain();
  const attack = opts.attackSec ?? 0.006;
  const release = opts.releaseSec ?? Math.max(0.02, opts.durationSec * 0.35);
  const sustainUntil = Math.max(opts.startSec + attack, opts.startSec + opts.durationSec - release);

  gain.gain.setValueAtTime(0, opts.startSec);
  gain.gain.linearRampToValueAtTime(opts.peakGain, opts.startSec + attack);
  gain.gain.setValueAtTime(opts.peakGain, sustainUntil);
  gain.gain.linearRampToValueAtTime(0, opts.startSec + opts.durationSec);

  osc.connect(gain);
  gain.connect(destination);
  osc.start(opts.startSec);
  osc.stop(opts.startSec + opts.durationSec + 0.03);
}

/** Play one composed note (`composition.ts`'s `NoteEvent`) on its own voice. */
export function playVoiceNote(
  ctx: BaseAudioContext,
  destination: AudioNode,
  opts: { voice: VoiceId; freq: number; startSec: number; durationSec: number; velocity: number },
): void {
  playTone(ctx, destination, {
    freq: opts.freq,
    type: VOICE_TYPE[opts.voice],
    startSec: opts.startSec,
    durationSec: opts.durationSec,
    peakGain: Math.max(0, Math.min(1, opts.velocity)) * VOICE_GAIN_SCALE[opts.voice],
  });
}

export interface SweepOptions {
  readonly fromFreq: number;
  readonly toFreq: number;
  readonly type: OscType;
  readonly startSec: number;
  readonly durationSec: number;
  readonly peakGain: number;
}

/** A pitch sweep, exponential (the way pitch is actually perceived) — thunks, zaps, stings. */
export function playSweep(ctx: BaseAudioContext, destination: AudioNode, opts: SweepOptions): void {
  const osc = ctx.createOscillator();
  osc.type = opts.type;
  const from = Math.max(1, opts.fromFreq);
  const to = Math.max(1, opts.toFreq);
  osc.frequency.setValueAtTime(from, opts.startSec);
  osc.frequency.exponentialRampToValueAtTime(to, opts.startSec + opts.durationSec);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(Math.max(0.0001, opts.peakGain), opts.startSec);
  gain.gain.exponentialRampToValueAtTime(0.0001, opts.startSec + opts.durationSec);

  osc.connect(gain);
  gain.connect(destination);
  osc.start(opts.startSec);
  osc.stop(opts.startSec + opts.durationSec + 0.03);
}

/** One second of white noise, generated once and reused as the source buffer for every noise burst on a given context. */
const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>();

function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const existing = noiseBuffers.get(ctx);
  if (existing) return existing;
  const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffers.set(ctx, buffer);
  return buffer;
}

export interface NoiseOptions {
  readonly startSec: number;
  readonly durationSec: number;
  readonly peakGain: number;
  /** `'highpass'` for hats/clicks, `'lowpass'` for thumps, `'bandpass'` for snares. Omit for unfiltered noise. */
  readonly filter?: 'highpass' | 'lowpass' | 'bandpass';
  readonly filterHz?: number;
}

/** A short burst of filtered noise — hats, snares, the hard-drop thunk, the garbage-warning hiss. */
export function playNoiseBurst(
  ctx: BaseAudioContext,
  destination: AudioNode,
  opts: NoiseOptions,
): void {
  const source = ctx.createBufferSource();
  source.buffer = noiseBuffer(ctx);
  source.loop = true;

  let chain: AudioNode = source;
  if (opts.filter) {
    const biquad = ctx.createBiquadFilter();
    biquad.type = opts.filter;
    biquad.frequency.value = opts.filterHz ?? 4000;
    chain.connect(biquad);
    chain = biquad;
  }

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.0001, opts.startSec);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, opts.peakGain), opts.startSec + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, opts.startSec + opts.durationSec);

  chain.connect(gain);
  gain.connect(destination);
  source.start(opts.startSec);
  source.stop(opts.startSec + opts.durationSec + 0.02);
}

/** A low sine thump with a fast pitch drop — the kick drum, and the basis of the hard-drop thunk. */
export function playKick(
  ctx: BaseAudioContext,
  destination: AudioNode,
  startSec: number,
  velocity: number,
): void {
  playSweep(ctx, destination, {
    fromFreq: 150,
    toFreq: 45,
    type: 'sine',
    startSec,
    durationSec: 0.16,
    peakGain: 0.9 * velocity,
  });
}

/** A tonal thump plus a noise burst — the snare/clap. */
export function playSnare(
  ctx: BaseAudioContext,
  destination: AudioNode,
  startSec: number,
  velocity: number,
): void {
  playTone(ctx, destination, {
    freq: 190,
    type: 'triangle',
    startSec,
    durationSec: 0.09,
    peakGain: 0.35 * velocity,
  });
  playNoiseBurst(ctx, destination, {
    startSec,
    durationSec: 0.1,
    peakGain: 0.4 * velocity,
    filter: 'bandpass',
    filterHz: 1800,
  });
}

/** A very short high-passed noise tick — the hi-hat. */
export function playHat(
  ctx: BaseAudioContext,
  destination: AudioNode,
  startSec: number,
  velocity: number,
): void {
  playNoiseBurst(ctx, destination, {
    startSec,
    durationSec: 0.045,
    peakGain: 0.22 * velocity,
    filter: 'highpass',
    filterHz: 7000,
  });
}
